/**
 * Error Logging Module
 * Main entry point for frontend error capture
 */

// Internal imports for initialization
import { installGlobalHandlers, installNetworkInterceptor } from './globalHandlers';
import { installTrustedScriptCors } from './trustedScriptCors';
import { setupAutoBreadcrumbs } from './breadcrumbs';

// Re-export capture functions (captureError used by ErrorBoundary)
export { captureError, logError, setCurrentRoute } from './captureError';

// Re-export console/network capture callbacks (wired into contextCapture)
export { handleCallableHedge, handleConsoleError, handleNetworkError, handleNetworkSuccess, shouldCaptureNetworkErrorBody } from './consoleAndNetworkCapture';

// Re-export formatting functions (copyErrorToClipboard used by ErrorAnalytics)
export { copyErrorToClipboard } from './formatForClaude';

// Re-export types
export type {
  ErrorSeverity,
  ErrorCategory,
  CapturePoint,
  Breadcrumb,
  CaptureErrorInput,
  ErrorPayload,
  ErrorLoggingConfig,
} from './types';

export type { ErrorForClaude } from './formatForClaude';

/**
 * Initialize error logging
 * Call this once at app startup
 */
export function initializeErrorLogging(): () => void {
  const cleanupFns: Array<() => void> = [];

  // Before anything can inject the Maps script: a throw inside Google's script then reports its real message (see trustedScriptCors.ts).
  cleanupFns.push(installTrustedScriptCors());

  // Install global error handlers
  cleanupFns.push(installGlobalHandlers());

  // Install network interceptor
  cleanupFns.push(installNetworkInterceptor());

  // Setup auto breadcrumb collection
  cleanupFns.push(setupAutoBreadcrumbs());

  console.debug('[ErrorLogging] Initialized');

  // Return cleanup function
  return () => {
    cleanupFns.forEach(fn => fn());
    console.debug('[ErrorLogging] Cleaned up');
  };
}
